import { ref, computed } from "vue";
import moment from "moment";
import engine from "../core/DataApi";

// ─────────────────────────────────────────────────────────────────────────
// Module « Logs Web » — logique (state + actions)
// But : visualiser / suivre ce qui se passe sur les différents sites web à
// partir de la table FileMaker WEB_LOGS.
// Tout le front est prêt ; le back (endpoints /api/web-logs/...) est à
// finaliser. Le chargement est tolérant : il accepte plusieurs formes de
// réponse FileMaker et refiltre côté client.
// ─────────────────────────────────────────────────────────────────────────
export default function () {
    // Données brutes chargées depuis le back (déjà aplaties)
    const logs = ref([]);
    const isLoading = ref(false);

    // Filtres de l'interface
    const filters = ref({
        search: "",
        niveau: [],      // multi-sélection (INFO / WARN / ERROR / DEBUG)
        statut: "",      // texte / code HTTP (ex : 200, 404, 500)
        site: null,      // hôte extrait du path (ex : velo-test2.accen.me)
        client: null,    // client (IP)
        user: null,      // utilisateur
        dateFrom: null,  // timestamp (DatePicker) début
        dateTo: null,    // timestamp (DatePicker) fin
    });

    // Détail (modale)
    const selectedLog = ref(null);
    const isDetailOpen = ref(false);

    // Rafraîchissement automatique
    const autoRefresh = ref(false);

    // Retour visuel
    const snackbar = ref({ show: false, text: "", color: "" });
    const showMessage = function (text, color) {
        snackbar.value = { show: true, text: text, color: color };
    };

    // ── Normalisation des réponses FileMaker ────────────────────────────────
    // Selon l'endpoint, FileMaker renvoie soit un tableau direct, soit
    // data.data.data[] (objets { fieldData, recordId }). On gère les deux.
    const extractRows = function (data) {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        const candidates = [
            data.records,
            data.rows,
            data.data,
            data.data && data.data.data,
            data.data && data.data.data && data.data.data.data,
        ];
        for (let i = 0; i < candidates.length; i++) {
            if (Array.isArray(candidates[i])) return candidates[i];
        }
        return [];
    };

    // Transforme une ligne FileMaker en objet log « plat » et homogène.
    const mapRow = function (row, index) {
        const fd = row && row.fieldData ? row.fieldData : (row || {});
        const recordId = row && row.recordId ? row.recordId : null;
        return {
            id: fd["Clé primaire"] || fd.cle_primaire || recordId || index,
            recordId: recordId,
            horodatage: fd["Horodatage de création"] || fd.horodatage_creation || "",
            cree_par: fd["Créé par"] || fd.cree_par || "",
            modifie_par: fd["Modifié par"] || fd.modifie_par || "",
            horodatage_modification: fd["Horodatage de modification"] || "",
            request: fd.request || "",
            statut: fd.statut !== undefined && fd.statut !== null ? String(fd.statut) : "",
            body: fd.body || "",
            client: fd.client || "",
            user: fd.user || "",
            response: fd.response || "",
            path: fd.path || "",
            duree_ms: fd.duree_ms !== undefined && fd.duree_ms !== null ? String(fd.duree_ms) : "",
            niveau: fd.niveau || "",
            id_correlation: fd.id_correlation || "",
            code_fm: fd.code_fm || "",
            erreur_message: fd.erreur_message || "",
            _raw: fd,
        };
    };

    const normalizeRecords = function (data) {
        const rows = extractRows(data);
        return rows.map(mapRow);
    };

    // ── Chargement ──────────────────────────────────────────────────────────
    const loadLogs = function () {
        isLoading.value = true;
        // On envoie les filtres au back (il pourra en tenir compte plus tard) ;
        // le front refiltre de toute façon via filteredLogs.
        engine.get_logs(buildBackendFilters(), function (data) {
            isLoading.value = false;
            console.log("get_logs", data);
            if (data && data.code === 0) {
                logs.value = normalizeRecords(data);
            } else if (data && data.code === -1) {
                // Endpoint pas encore dispo : on tente le "tout charger".
                loadLogsAll();
            } else {
                logs.value = normalizeRecords(data);
            }
        });
    };

    // Repli : charge tous les logs sans filtre serveur.
    const loadLogsAll = function () {
        isLoading.value = true;
        engine.get_logs_all(function (data) {
            isLoading.value = false;
            console.log("get_logs_all", data);
            if (data && data.code === -1) {
                showMessage("Impossible de charger les logs (back-end non disponible).", "error");
                logs.value = [];
                return;
            }
            logs.value = normalizeRecords(data);
        });
    };

    // Prépare l'objet de filtres envoyé au back.
    const buildBackendFilters = function () {
        const f = filters.value;
        return {
            search: f.search || "",
            niveau: f.niveau || [],
            statut: f.statut || "",
            site: f.site || "",
            client: f.client || "",
            user: f.user || "",
            dateFrom: f.dateFrom || null,
            dateTo: f.dateTo || null,
        };
    };

    const refresh = function () {
        loadLogs();
    };

    const resetFilters = function () {
        filters.value = { search: "", niveau: [], statut: "", site: null, client: null, user: null, dateFrom: null, dateTo: null };
    };

    // ── Détail ───────────────────────────────────────────────────────────────
    const openDetail = function (row) {
        selectedLog.value = row;
        isDetailOpen.value = true;
    };
    const closeDetail = function () {
        isDetailOpen.value = false;
    };

    // ── Suppression ───────────────────────────────────────────────────────────
    const deleteLog = function (row) {
        if (!row) return;
        engine.delete_log(row.id, function (data) {
            if (data && data.code === 0) {
                logs.value = logs.value.filter((l) => l.id !== row.id);
                showMessage("Log supprimé.", "success");
            } else {
                showMessage("Échec de la suppression du log.", "error");
            }
        });
    };

    // ── Helpers d'affichage ───────────────────────────────────────────────────
    const parseDate = function (v) {
        if (v === undefined || v === null || v === "") return null;
        // Timestamp numérique (ms ou s)
        if (!isNaN(v) && String(v).trim() !== "") {
            let n = parseInt(v);
            if (n < 1e12) n = n * 1000; // secondes → ms
            const d = new Date(n);
            if (!isNaN(d.getTime())) return d;
        }
        // Formats FileMaker courants
        const formats = [
            "MM/DD/YYYY HH:mm:ss", "MM/DD/YYYY HH:mm", "DD/MM/YYYY HH:mm:ss",
            "DD/MM/YYYY HH:mm", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss",
            "MM/DD/YYYY", "DD/MM/YYYY", "YYYY-MM-DD",
        ];
        const m = moment(v, formats, true);
        if (m.isValid()) return m.toDate();
        const m2 = moment(new Date(v));
        return m2.isValid() ? m2.toDate() : null;
    };

    const formatTs = function (v) {
        const d = parseDate(v);
        if (!d) return v || "";
        return moment(d).format("DD/MM/YYYY HH:mm:ss");
    };

    const normalizeLevel = function (niveau) {
        return (niveau || "").toString().trim().toUpperCase();
    };

    const levelColor = function (niveau) {
        const n = normalizeLevel(niveau);
        if (n.indexOf("ERR") === 0 || n === "FATAL" || n === "CRITICAL") return "#EF5350";
        if (n.indexOf("WARN") === 0 || n === "ATTENTION") return "#FB8C00";
        if (n === "INFO" || n === "SUCCESS" || n === "OK") return "#43A047";
        if (n.indexOf("DEBUG") === 0 || n === "TRACE") return "#78909C";
        return "#90A4AE";
    };

    const statutColor = function (statut) {
        const n = parseInt(statut);
        if (isNaN(n)) return "#90A4AE";
        if (n < 300) return "#43A047";
        if (n < 400) return "#5C6BC0";
        if (n < 500) return "#FB8C00";
        return "#EF5350";
    };

    const isError = function (l) {
        const n = normalizeLevel(l.niveau);
        const s = parseInt(l.statut);
        return (
            n.indexOf("ERR") === 0 || n === "FATAL" || n === "CRITICAL" ||
            (!isNaN(s) && s >= 500) ||
            (l.erreur_message && l.erreur_message.length > 0)
        );
    };
    const isWarning = function (l) {
        const n = normalizeLevel(l.niveau);
        const s = parseInt(l.statut);
        return n.indexOf("WARN") === 0 || n === "ATTENTION" || (!isNaN(s) && s >= 400 && s < 500);
    };

    // ── Filtrage côté client ──────────────────────────────────────────────────
    const filteredLogs = computed(() => {
        let list = logs.value.slice();
        const f = filters.value;

        // Recherche plein texte (sur les champs utiles)
        if (f.search && f.search.trim().length > 0) {
            const q = f.search.trim().toUpperCase();
            list = list.filter((l) => {
                const hay = [
                    l.request, l.path, l.client, l.user, l.statut, l.niveau,
                    l.code_fm, l.id_correlation, l.erreur_message, l.body, l.response,
                ].join(" ").toUpperCase();
                return hay.indexOf(q) !== -1;
            });
        }

        // Niveau (multi)
        if (f.niveau && f.niveau.length > 0) {
            const wanted = f.niveau.map((n) => n.toUpperCase());
            list = list.filter((l) => wanted.includes(normalizeLevel(l.niveau)));
        }

        // Statut (préfixe : "5" matche 5xx, "404" matche 404)
        if (f.statut && String(f.statut).trim().length > 0) {
            const s = String(f.statut).trim();
            list = list.filter((l) => String(l.statut).indexOf(s) === 0);
        }

        // Site (hôte extrait du path)
        if (f.site) {
            list = list.filter((l) => siteHost(l.path) === f.site);
        }

        // Client (IP)
        if (f.client) {
            list = list.filter((l) => clientIp(l.client) === f.client);
        }

        // User
        if (f.user) {
            list = list.filter((l) => l.user === f.user);
        }

        // Plage de dates (sur l'horodatage de création)
        if (f.dateFrom) {
            const from = parseDate(f.dateFrom);
            if (from) list = list.filter((l) => {
                const d = parseDate(l.horodatage);
                return d ? d.getTime() >= from.getTime() : true;
            });
        }
        if (f.dateTo) {
            const to = parseDate(f.dateTo);
            if (to) {
                // fin de journée incluse
                to.setHours(23, 59, 59, 999);
                list = list.filter((l) => {
                    const d = parseDate(l.horodatage);
                    return d ? d.getTime() <= to.getTime() : true;
                });
            }
        }

        // Tri : plus récent en premier
        list.sort((a, b) => {
            const da = parseDate(a.horodatage);
            const db = parseDate(b.horodatage);
            return (db ? db.getTime() : 0) - (da ? da.getTime() : 0);
        });

        return list;
    });

    // ── KPI (calculés à partir des logs filtrés, sans dépendre du back) ────────
    const stats = computed(() => {
        const list = filteredLogs.value;
        let errors = 0, warnings = 0, durSum = 0, durCount = 0;
        list.forEach((l) => {
            if (isError(l)) errors++;
            else if (isWarning(l)) warnings++;
            const d = parseInt(l.duree_ms);
            if (!isNaN(d)) { durSum += d; durCount++; }
        });
        return {
            total: list.length,
            errors: errors,
            warnings: warnings,
            avgDuration: durCount > 0 ? Math.round(durSum / durCount) : 0,
        };
    });

    // Niveaux présents (pour proposer le filtre dynamiquement)
    const levelOptions = computed(() => {
        const set = {};
        logs.value.forEach((l) => { const n = normalizeLevel(l.niveau); if (n) set[n] = true; });
        const found = Object.keys(set);
        // Valeurs par défaut si rien encore chargé
        const base = ["INFO", "WARN", "ERROR", "DEBUG"];
        base.forEach((b) => { if (!set[b]) found.push(b); });
        return [...new Set(found)];
    });

    // ── Helpers d'affichage compact (pour ne pas déformer le tableau) ─────────
    // Le champ "client" contient IP(s) + User-Agent, séparés par " · ".
    const parseClient = function (client) {
        const raw = (client || "").toString();
        if (!raw) return { ip: "", agent: "" };
        const idx = raw.indexOf(" · ");
        if (idx === -1) {
            // Pas de séparateur : on tente de couper avant "Mozilla".
            const m = raw.indexOf("Mozilla");
            if (m > 0) return { ip: raw.slice(0, m).trim().replace(/,\s*$/, ""), agent: raw.slice(m).trim() };
            return { ip: raw.trim(), agent: "" };
        }
        return { ip: raw.slice(0, idx).trim(), agent: raw.slice(idx + 3).trim() };
    };

    // IP unique et lisible (déduplique "x, x").
    const clientIp = function (client) {
        const ip = parseClient(client).ip;
        if (!ip) return "";
        const parts = ip.split(",").map((p) => p.trim()).filter((p) => p.length > 0);
        return [...new Set(parts)].join(", ");
    };

    // Navigateur + OS résumés à partir du User-Agent.
    const clientAgentShort = function (client) {
        const ua = parseClient(client).agent;
        if (!ua) return "";
        let browser = "";
        if (/Edg/i.test(ua)) browser = "Edge";
        else if (/OPR|Opera/i.test(ua)) browser = "Opera";
        else if (/Chrome/i.test(ua)) browser = "Chrome";
        else if (/Firefox/i.test(ua)) browser = "Firefox";
        else if (/Safari/i.test(ua)) browser = "Safari";
        let os = "";
        if (/iPhone/i.test(ua)) os = "iPhone";
        else if (/iPad/i.test(ua)) os = "iPad";
        else if (/Android/i.test(ua)) os = "Android";
        else if (/Mac OS X|Macintosh/i.test(ua)) os = "macOS";
        else if (/Windows/i.test(ua)) os = "Windows";
        else if (/Linux/i.test(ua)) os = "Linux";
        return [browser, os].filter((v) => v).join(" · ");
    };

    // Hôte (site) extrait du champ path (ex : https://velo-test2.accen.fr/... → velo-test2.accen.fr)
    const siteHost = function (path) {
        const raw = (path || "").toString();
        const m = raw.match(/https?:\/\/([^/\s,]+)/i);
        if (m && m[1]) return m[1];
        return raw.length > 40 ? raw.slice(0, 40) + "…" : raw;
    };

    // Méthode HTTP extraite du champ request (GET/POST/PUT/DELETE…).
    const requestMethod = function (request) {
        const m = (request || "").toString().trim().match(/^(GET|POST|PUT|PATCH|DELETE|OPTIONS|HEAD)/i);
        return m ? m[1].toUpperCase() : "";
    };
    // Reste de la requête (route) sans la méthode.
    const requestPath = function (request) {
        const raw = (request || "").toString().trim();
        const method = requestMethod(request);
        return method ? raw.slice(method.length).trim() : raw;
    };
    const methodColor = function (request) {
        const m = requestMethod(request);
        if (m === "GET") return "#43A047";
        if (m === "POST") return "#5C6BC0";
        if (m === "PUT" || m === "PATCH") return "#FB8C00";
        if (m === "DELETE") return "#EF5350";
        return "#90A4AE";
    };

    // ── Listes déroulantes (valeurs distinctes présentes dans les logs) ───────
    const siteOptions = computed(() => {
        const set = {};
        logs.value.forEach((l) => { const s = siteHost(l.path); if (s) set[s] = true; });
        return Object.keys(set).sort();
    });
    const clientOptions = computed(() => {
        const set = {};
        logs.value.forEach((l) => { const c = clientIp(l.client); if (c) set[c] = true; });
        return Object.keys(set).sort();
    });
    const userOptions = computed(() => {
        const set = {};
        logs.value.forEach((l) => { if (l.user) set[l.user] = true; });
        return Object.keys(set).sort();
    });

    // ── Init ───────────────────────────────────────────────────────────────
    loadLogs();

    return {
        // state
        logs,
        isLoading,
        filters,
        siteOptions,
        clientOptions,
        userOptions,
        levelOptions,
        selectedLog,
        isDetailOpen,
        autoRefresh,
        snackbar,
        // derived
        filteredLogs,
        stats,
        // actions
        loadLogs,
        loadLogsAll,
        refresh,
        resetFilters,
        openDetail,
        closeDetail,
        deleteLog,
        // helpers
        formatTs,
        levelColor,
        statutColor,
        isError,
        isWarning,
        clientIp,
        clientAgentShort,
        siteHost,
        requestMethod,
        requestPath,
        methodColor,
    };
}
